import { convexQuery } from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { useEffect, type PropsWithChildren } from 'react';
import { api } from '~/convex/_generated/api';

// For more information about the App Tracking Transparency, see
// https://developer.apple.com/documentation/apptrackingtransparency

const AppTrackingTransparencyStatusQueryKey = ['app-tracking-transparency-key'];

/**
 * A custom hook to retrieve the current App Tracking Transparency authorization
 * status.
 *
 * Returns the current tracking authorization status (e.g., 'authorized',
 * 'denied', 'notDetermined', 'restricted').
 */
export const useGetAppTrackingTransparency = () => {
  const { data, status, ...rest } = useQuery({
    queryKey: AppTrackingTransparencyStatusQueryKey,
    queryFn: async () => {
      const { status } = await AppTrackingTransparency.getStatus();
      return status;
    },
  });

  return { status: data, queryStatus: status, ...rest };
};

/**
 * A custom hook to request App Tracking Transparency permission from the user.
 *
 * This hook uses `useMutation` to handle the asynchronous permission request.
 * It includes logic to prevent unnecessary prompts:
 * 1. Checks the current status first.
 * 2. If the status is already determined (authorized, denied, or restricted),
 *    it returns the current status without prompting.
 * 3. If the status is 'notDetermined', it shows the system permission prompt.
 */
export const useRequestAppTrackingPermission = () => {
  const { mutate, ...rest } = useMutation({
    mutationFn: async () => {
      // Get current app tracking transparency status. If the user has already
      // authorized or denied the permission, don't request permission.
      const { status } = await AppTrackingTransparency.getStatus();
      if (status !== 'notDetermined') return status;

      // Request the permission otherwise
      const response = await AppTrackingTransparency.requestPermission();
      return response;
    },
  });

  return { requestAppTrackingPermission: mutate, ...rest };
};

/**
 * A "Side Effect" Guard component.
 * * It renders its children immediately but triggers the native App Tracking
 *   Transparency (ATT) permission request once the Convex user query
 *   successfully resolves with a valid user ID.
 *
 * Note: This component does not block rendering.
 */
export const AppTrackingTransparencyGuard = ({
  children,
}: PropsWithChildren) => {
  const { data: user, isSuccess } = useQuery(
    convexQuery(api.user.currentUser, {}),
  );

  const { requestAppTrackingPermission } = useRequestAppTrackingPermission();

  useEffect(() => {
    // Only request permission if the query succeeded and we have a valid user ID.
    // The OS handles the logic of not showing the alert twice if already decided.
    if (isSuccess && user?._id) {
      requestAppTrackingPermission();
    }
  }, [user?._id, isSuccess, requestAppTrackingPermission]);

  return <>{children}</>;
};
